import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignCommercialCategoryRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignCommercialCategories']

type UseCampaignCommercialCategoriesOptions = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignCommercialCategoryRequestOptions,
}

function useCampaignCommercialCategories(options: UseCampaignCommercialCategoriesOptions) {
  const { performHttpRequests = false, requestOptions } = options

  const {
    updatedEntities: { campaignCommercialCategories: campaignCommercialCategoriesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignCommercialCategories } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignCommercialCategories = useMemo(
    () => sortArrayBy(Object.values(campaignCommercialCategories), 'asc', 'title'),
    [campaignCommercialCategoriesUpdatedAt],
  )

  const filteredCampaignCommercialCategoriesCount = filteredCampaignCommercialCategories.length
  const hasFilteredCampaignCommercialCategories = !!filteredCampaignCommercialCategoriesCount

  const { loading: loadingCampaignCommercialCategories } = useReduxAction(
    'campaignCommercialCategories',
    'loadCampaignCommercialCategories',
    {
      ...requestOptions,
    },
    [performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    filteredCampaignCommercialCategories,
    filteredCampaignCommercialCategoriesCount,
    hasFilteredCampaignCommercialCategories,
    loading: loadingCampaignCommercialCategories,
  }
}

export default useCampaignCommercialCategories
